import { useEffect, useState } from "react";
import { ReportType } from "../../models/enumerations/report-type";
import { ReportAssessmentData } from "../../models/interfaces/reports/report-assessment-data";
import { ReportComplianceData } from "../../models/interfaces/reports/report-compliance-data";
import { ReportDetailData } from "../../models/interfaces/reports/report-detail-data";
import { ReportSummaryData } from "../../models/interfaces/reports/report-summary-data";
import FunctionService from "../../utilities/services/function-service";
import { Loader } from "../loader/loader";
import ReportAssessment from "./report-assessment";
import ReportCompliance from "./report-compliance";
import ReportDetail from "./report-detail";
import ReportScore from "./report-score";
import ReportSummary from "./report-summary";
import ReportMovement from "./report-movement";
import { ReportMovementData } from "../../models/interfaces/reports/report-movement-data";
import ReportWellnessSummary from "./report-wellness-summary";
import { ReportWellnessSummaryData } from "../../models/interfaces/reports/report-wellness-summary-data";
import { MskScoreVersion } from "../../models/enumerations/msk-score-versions";
import ReportWellness from "./report-wellness";
import { ReportWellnessDataV1, ReportWellnessDataV2 } from "../../models/interfaces/reports/report-wellness-data-versioned";
import { createLogger } from "../../utilities/logger";

const log = createLogger("ReportRunner");

// -----------------------------------------------------------------------------------------
// #region Intefaces
// -----------------------------------------------------------------------------------------

interface ReportProps {
    reportId: string;
    type: ReportType;
    userId: string;
}

// #endregion Intefaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportRunner: React.FC<ReportProps> = (props: ReportProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [reportData, setReportData] = useState<any>();

    useEffect(() => {
        if (props.reportId == null || props.userId == null) {
            return;
        }

        FunctionService.getReportData(
            props.reportId,
            props.userId,
        ).then(data => {
            setReportData(data);
            setIsLoading(false);
            log.info("Report data loaded", {
                reportId: props.reportId,
                reportType: props.type,
            });
        }).catch((e) => {
            log.error("Failed to load report data", {
                exception: e,
                reportId: props.reportId,
                reportType: props.type,
            });
            setIsLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    let reportComponent = null;
    if (reportData) {
        switch (props.type) {
            case ReportType.Compliance:
                reportComponent = <ReportCompliance
                    data={reportData as ReportComplianceData} />
                break;
            case ReportType.Detail:
                reportComponent = <ReportDetail
                    data={reportData as ReportDetailData} />
                break;
            case ReportType.Score:
                reportComponent = <ReportScore
                    data={reportData as ReportDetailData} />
                break;
            case ReportType.Summary:
                reportComponent = <ReportSummary
                    data={reportData as ReportSummaryData} />
                break;
            case ReportType.Assessment:
                reportComponent = <ReportAssessment
                    data={reportData as ReportAssessmentData} />
                break;
            case ReportType.Wellness:
                if (reportData.mskScoreVersion === MskScoreVersion.V2) {
                    reportComponent = <ReportWellness
                        data={reportData as ReportWellnessDataV2} />
                    break;
                }
                else {
                    reportComponent = <ReportWellness
                        data={reportData as ReportWellnessDataV1} />
                }
                break;
            case ReportType.Movement:
                reportComponent = <ReportMovement
                    data={reportData as ReportMovementData} />
                break;
            case ReportType.WellnessSummary:
                reportComponent = <ReportWellnessSummary
                    data={reportData as ReportWellnessSummaryData} />
                break;
        }
    }

    return (
        <div>
            <Loader
                isVisible={isLoading} />
            {reportComponent ?? <div>No report available {reportData?.id} {reportData?.name} {props.reportId} {props.type} {props.userId}</div>}
        </div>
    );
}

export default ReportRunner;